'use client'

import { useEffect, useRef, useState } from 'react'
import RichText from '@/components/RichText'
import type { MovinghubWidgetBlock as MovinghubWidgetBlockProps } from '@/payload-types'

/**
 * Movinghub embedded utility-connection widget — same integration as the old WP
 * site's "Wilson Client Care" page: Movinghub's handshake script acts as an
 * availability probe (it appends a "powered by" image under the widget), then
 * the hosted form is iframed in from au-apps.utilihub.io with their
 * iframe-resizer keeping the height in sync. If their scripts fail to load, the
 * old site's maintenance message shows instead.
 */

const UTILIHUB_BASE = 'https://au-apps.utilihub.io'
const MAINTENANCE_MESSAGE =
  'Unfortunately our system is currently down for Maintenance, should you wish to contact us please call 1300 661 448 Monday to Friday 9:00am to 7:00pm (Local Time).'

declare global {
  interface Window {
    movinghubHandDivId?: string
    iFrameResize?: (options: Record<string, unknown>, target?: HTMLIFrameElement) => void
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })
}

export function MovinghubWidgetBlock({ introContent, widgetPath }: MovinghubWidgetBlockProps) {
  const mountRef = useRef<HTMLDivElement>(null)
  const started = useRef(false)
  const [maintenance, setMaintenance] = useState(false)

  useEffect(() => {
    if (started.current || !widgetPath) return
    started.current = true

    // The handshake script reads this global to find its target div.
    window.movinghubHandDivId = 'movinghubHandDiv'

    Promise.all([
      loadScript(`${UTILIHUB_BASE}/assets/js/form-builder/widgets/movinghub.handshake.nologo.js`),
      loadScript(`${UTILIHUB_BASE}/assets/js/form-builder/widgets/iframe-resizer/iframeResizer.min.js`),
    ])
      .then(() => {
        const mount = mountRef.current
        if (!mount || mount.querySelector('iframe')) return
        const iframe = document.createElement('iframe')
        iframe.setAttribute('src', `${UTILIHUB_BASE}/form-builder-widgets/connect/${widgetPath}`)
        iframe.setAttribute('scrolling', 'no')
        iframe.setAttribute('frameborder', '0')
        iframe.setAttribute('width', '100%')
        iframe.setAttribute('style', 'border:none;width:100%;min-height:640px;')
        iframe.setAttribute('title', 'Wilson Property Concierge — utility connections')
        mount.appendChild(iframe)
        window.iFrameResize?.(
          {
            log: false,
            autoResize: true,
            checkOrigin: false,
            heightCalculationMethod: 'taggedElement',
            warningTimeout: 10000,
          },
          iframe,
        )
      })
      .catch(() => setMaintenance(true))
  }, [widgetPath])

  if (!widgetPath) return null

  return (
    <section className="mx-auto w-full max-w-[var(--container-content)] px-[var(--spacing-container)]">
      <div className="max-w-[704px]">
        {introContent && (
          <RichText
            className="mb-16 [&_p]:!text-body-md [&_p]:!font-light"
            data={introContent}
            enableGutter={false}
          />
        )}
        {maintenance ? (
          <p className="my-12 text-center text-body-md font-light">{MAINTENANCE_MESSAGE}</p>
        ) : (
          <>
            <div ref={mountRef} />
            <div id="movinghubHandDiv" className="text-center" />
          </>
        )}
      </div>
    </section>
  )
}
